/**
 * Resizable - Reusable wrapper that adds resize handles to any content
 *
 * Provides edge and corner drag handles for resizing, with localStorage persistence.
 * Shows grip indicators on hover of each individual handle.
 */

import React, { useState, useCallback, useRef, type ReactNode } from 'react';

export interface ResizableProps {
  nodeId: string;
  defaultWidth: number;
  defaultHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  children: (width: number, height: number) => ReactNode;
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

// Grip dot component for visual resize indicators
function GripDots({ direction }: { direction: 'horizontal' | 'vertical' | 'corner' }) {
  const dotStyle: React.CSSProperties = {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.5)'
  };

  if (direction === 'corner') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 3px)', gap: 2 }}>
        <div style={dotStyle} />
        <div style={dotStyle} />
        <div style={dotStyle} />
        <div style={dotStyle} />
      </div>
    );
  }

  const isHorizontal = direction === 'horizontal';
  return (
    <div style={{
      display: 'flex',
      flexDirection: isHorizontal ? 'row' : 'column',
      gap: 3
    }}>
      <div style={dotStyle} />
      <div style={dotStyle} />
      <div style={dotStyle} />
    </div>
  );
}

// Individual resize handle that reports hover to parent
interface ResizeHandleProps {
  onMouseDown: (e: React.MouseEvent) => void;
  onHoverChange: (hovered: boolean) => void;
  style: React.CSSProperties;
  gripStyle: React.CSSProperties;
  gripDirection: 'horizontal' | 'vertical' | 'corner';
  showGrip: boolean;
}

function ResizeHandle({ onMouseDown, onHoverChange, style, gripStyle, gripDirection, showGrip }: ResizeHandleProps) {
  return (
    <>
      <div
        className="nodrag nopan"
        onMouseDown={onMouseDown}
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
        style={style}
      />
      <div style={{
        ...gripStyle,
        opacity: showGrip ? 1 : 0,
        transition: 'opacity 0.15s ease'
      }}>
        <GripDots direction={gripDirection} />
      </div>
    </>
  );
}

export function Resizable({
  nodeId,
  defaultWidth,
  defaultHeight = 150,
  minWidth: minWidthProp,
  maxWidth = 800,
  minHeight = 80,
  maxHeight = 600,
  children
}: ResizableProps) {
  // Track how many handles are hovered (to handle overlapping hover areas)
  const [hoverCount, setHoverCount] = useState(0);
  const isAnyHandleHovered = hoverCount > 0;

  const handleHoverChange = useCallback((hovered: boolean) => {
    setHoverCount(c => hovered ? c + 1 : c - 1);
  }, []);

  // Min width defaults to container width
  const minWidth = minWidthProp ?? defaultWidth;

  // Persist size in localStorage per node
  const widthStorageKey = `vizWidth:${nodeId}`;
  const heightStorageKey = `vizHeight:${nodeId}`;

  const [width, setWidth] = useState(() => {
    try {
      const stored = localStorage.getItem(widthStorageKey);
      return stored ? Math.max(minWidth, Math.min(maxWidth, parseInt(stored, 10))) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });

  const [height, setHeight] = useState(() => {
    try {
      const stored = localStorage.getItem(heightStorageKey);
      return stored ? Math.max(minHeight, Math.min(maxHeight, parseInt(stored, 10))) : defaultHeight;
    } catch {
      return defaultHeight;
    }
  });

  // Store in refs to avoid stale closures
  const widthRef = useRef(width);
  const heightRef = useRef(height);
  const minWidthRef = useRef(minWidth);
  widthRef.current = width;
  heightRef.current = height;
  minWidthRef.current = minWidth;

  // Resize handle drag with direction
  const handleResizeMouseDown = useCallback((direction: ResizeDirection) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = widthRef.current;
    const startHeight = heightRef.current;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;

      // Handle horizontal resize
      if (direction.includes('e')) {
        newWidth = Math.max(minWidthRef.current, Math.min(maxWidth, startWidth + deltaX));
      } else if (direction.includes('w')) {
        newWidth = Math.max(minWidthRef.current, Math.min(maxWidth, startWidth - deltaX));
      }

      // Handle vertical resize
      if (direction.includes('s')) {
        newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));
      } else if (direction.includes('n')) {
        newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight - deltaY));
      }

      if (newWidth !== widthRef.current) {
        setWidth(newWidth);
        localStorage.setItem(widthStorageKey, String(newWidth));
      }
      if (newHeight !== heightRef.current) {
        setHeight(newHeight);
        localStorage.setItem(heightStorageKey, String(newHeight));
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widthStorageKey, heightStorageKey, maxWidth, minHeight, maxHeight]);

  const handleSize = 8;
  const cornerSize = 12;

  // Base grip style (without opacity - that's controlled per-handle)
  const baseGripStyle: React.CSSProperties = {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 11
  };

  return (
    <div style={{ position: 'relative', width, height }}>
      {/* Content container - fills the space, lower z-index than resize handles */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
        {children(width, height)}
      </div>

      {/* Right edge */}
      <ResizeHandle
        onMouseDown={handleResizeMouseDown('e')}
        onHoverChange={handleHoverChange}
        showGrip={isAnyHandleHovered}
        style={{
          position: 'absolute',
          top: cornerSize,
          right: 0,
          bottom: cornerSize,
          width: handleSize,
          cursor: 'ew-resize',
          zIndex: 10
        }}
        gripStyle={{ ...baseGripStyle, top: '50%', right: 2, transform: 'translateY(-50%)' }}
        gripDirection="vertical"
      />

      {/* Left edge */}
      <ResizeHandle
        onMouseDown={handleResizeMouseDown('w')}
        onHoverChange={handleHoverChange}
        showGrip={isAnyHandleHovered}
        style={{
          position: 'absolute',
          top: cornerSize,
          left: 0,
          bottom: cornerSize,
          width: handleSize,
          cursor: 'ew-resize',
          zIndex: 10
        }}
        gripStyle={{ ...baseGripStyle, top: '50%', left: 2, transform: 'translateY(-50%)' }}
        gripDirection="vertical"
      />

      {/* Bottom edge */}
      <ResizeHandle
        onMouseDown={handleResizeMouseDown('s')}
        onHoverChange={handleHoverChange}
        showGrip={isAnyHandleHovered}
        style={{
          position: 'absolute',
          bottom: 0,
          left: cornerSize,
          right: cornerSize,
          height: handleSize,
          cursor: 'ns-resize',
          zIndex: 10
        }}
        gripStyle={{ ...baseGripStyle, bottom: 2, left: '50%', transform: 'translateX(-50%)' }}
        gripDirection="horizontal"
      />

      {/* Bottom-right corner */}
      <ResizeHandle
        onMouseDown={handleResizeMouseDown('se')}
        onHoverChange={handleHoverChange}
        showGrip={isAnyHandleHovered}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: cornerSize,
          height: cornerSize,
          cursor: 'nwse-resize',
          zIndex: 10
        }}
        gripStyle={{ ...baseGripStyle, bottom: 2, right: 2 }}
        gripDirection="corner"
      />

      {/* Bottom-left corner */}
      <ResizeHandle
        onMouseDown={handleResizeMouseDown('sw')}
        onHoverChange={handleHoverChange}
        showGrip={isAnyHandleHovered}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: cornerSize,
          height: cornerSize,
          cursor: 'nesw-resize',
          zIndex: 10
        }}
        gripStyle={{ ...baseGripStyle, bottom: 2, left: 2 }}
        gripDirection="corner"
      />
    </div>
  );
}
